import { useCallback, useState, useMemo, type FC } from "react";
import { useCreateRemoteRunner, useListRunners, type PlainRunner } from "@/queries/runner-queries";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { NewAWSRunnerForm } from "@/components/runners/NewAWSRunnerForm";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { Button } from "@/components/flexkit/Button";
import { ExternalLink } from "@/components/podkit/typography/Link";
import { TrackLocations } from "@/hooks/use-segment";
import { useNavigate } from "react-router-dom";
import { TimelineContentContainer } from "@/components/onboarding/TimelineContentContainer";
import { ContinueWithExistingRunners } from "@/components/onboarding/ContinueWithExistingRunners";
import { Banner } from "@/components/flexkit/Banner";
import { RunnerInfoCard } from "@/components/onboarding/RunnerInfoCard";
import { formatError } from "@/utils/errors";
import { useToast } from "@/components/podkit/toasts/use-toast";

export const CreateAWSRunnerPage: FC = () => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const createRunner = useCreateRemoteRunner();
    const [createDisabled, setCreateDisabled] = useState(false);
    const [runner, setRunner] = useState<PlainRunner | undefined>();
    const { data: remoteRunners } = useListRunners({ provider: RunnerProvider.AWS_EC2 });

    const hasExistingRunner = useMemo(() => !!remoteRunners?.length, [remoteRunners]);

    const onBack = useCallback(() => {
        navigate("/onboarding/set-up-runner/remote/provider");
    }, [navigate]);

    const onContinue = useCallback(() => {
        if (!runner) {
            return;
        }
        navigate(`/onboarding/set-up-runner/remote/configure/${runner.runnerId}/install`);
    }, [navigate, runner]);

    const handleCreateRunner = useCallback<(values: { name: string; region: string }) => Promise<void>>(
        async ({ name, region }) => {
            try {
                const { runner } = await createRunner.mutateAsync({ name, region, provider: RunnerProvider.AWS_EC2 });
                setRunner(runner);
            } catch (error) {
                toast({
                    title: "We couldn't create a runner. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [createRunner, setRunner, toast],
    );

    return (
        <TimelineContentContainer
            data-track-location={TrackLocations.OnboardingRunnerCreateRunnerTabStep}
            onBack={onBack}
            onNext={onContinue}
            onNextDisabled={!runner}
        >
            <div className="flex w-full flex-col gap-4 lg:flex-row">
                <div className="flex basis-1/2 flex-col items-start gap-2">
                    <Heading2>Create AWS runner</Heading2>
                    <Text className="text-base text-content-secondary">
                        To continue you will need an{" "}
                        <ExternalLink href="https://aws.amazon.com/resources/create-account/">AWS account</ExternalLink>
                        . The AWS Free Tier covers the fixed-cost of the AWS runner (not that of environments).
                    </Text>
                    <Text className="text-base text-content-secondary">
                        Choose the AWS region to deploy the runner into.
                    </Text>
                </div>
                {runner ? (
                    <div className="flex w-full basis-1/2 flex-col gap-2 lg:min-w-[495px]">
                        <Banner
                            text="Runner ready for installation. Click Continue to set up your runner"
                            variant="info"
                        />
                        <RunnerInfoCard runner={runner} showInstallButton={false} />
                    </div>
                ) : (
                    <div className="flex w-full basis-1/2 flex-col gap-4 rounded-lg border-0.5 border-border-base p-4 pr-6 lg:min-w-[495px]">
                        {hasExistingRunner && (
                            <>
                                <div className="flex flex-col gap-2">
                                    <Text className="text-lg font-bold text-content-primary">
                                        Continue with an existing runner
                                    </Text>
                                    <ContinueWithExistingRunners runners={remoteRunners ?? []} />
                                </div>
                                <Text className="text-lg font-bold text-content-primary">Or, create a new runner</Text>
                            </>
                        )}

                        <div className="flex flex-col gap-4">
                            <NewAWSRunnerForm
                                formId="new-remote-runner"
                                onDisabledChange={setCreateDisabled}
                                onSubmit={handleCreateRunner}
                                className="flex-col *:flex-grow"
                            />
                            <Button
                                type="submit"
                                form="new-remote-runner"
                                variant="primary"
                                disabled={createDisabled}
                                loading={createRunner.isPending}
                                aria-label="Create"
                                className="w-fit"
                            >
                                Create Runner
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </TimelineContentContainer>
    );
};
